import { selectUserId } from "@common/reducers/UserSelectors";
import type { CommonRootState } from "@common/types/common-root-state-type";
import { SendResponseFunction } from "@common/services/messageService";
import store from "@extension-context-common/store/extension-store";
import { RawWaStateStatus } from "@bt-common/interfaces/message-service-interface";
import { SessionEventService } from "./session-event-service";
import { SessionStatusAutomaton } from "./session-status-automaton";
import { AccountHealthReporter } from "./account-health-reporter";

import ObjectId from 'bson-objectid';
import { initAuth } from "@/common/bt-auth/AuthThunks";

class WaStateManager {
  private static instance: WaStateManager;
  private localSessionId: string;

  private sessionId: string;
  private podId: string;
  private userId: string;

  private constructor() {

    this.localSessionId = this.getLocalSessionId();
    this.sessionId = window.localStorage.getItem("sessionId") || '';
    this.podId = window.localStorage.getItem("podId") || '';

    this.userId = selectUserId(store.getState() as object as CommonRootState) ?? '';
    SessionEventService.getInstance().setSessionContext(this.userId, this.sessionId, this.localSessionId, this.podId);

    SessionStatusAutomaton.getInstance().setSessionContext(this.userId, this.sessionId, this.localSessionId, this.podId);

    // WhatsApp account-health probe (restriction + send quota). Armed HERE, at content boot,
    // because this constructor runs for every extension user — including plain (non-gateway)
    // profiles, which have no session doc and whose automaton never reaches its CONNECTED
    // branch, so the connect-edge arming alone silently skipped them.
    AccountHealthReporter.getInstance().start();
  }

  public static getInstance(): WaStateManager {
    if (!WaStateManager.instance) {
      WaStateManager.instance = new WaStateManager();
    }
    return WaStateManager.instance;
  }


  public async handleWaStateChanges(rawWaStateStatus: RawWaStateStatus, triggerdBy = "", forceUpdate = false): Promise<void> {

    // if (!this.userId) {
    //   console.log('[waStateManager] (handleWaStateChanges) User ID not found');
    //   return;
    // }

    SessionEventService.getInstance().updateSessionEventBuffer({
      state: rawWaStateStatus.state,
      stream: rawWaStateStatus.stream,
      hasSynced: rawWaStateStatus.hasSynced,
      qrcode: rawWaStateStatus.qrcode,
      triggerdBy: triggerdBy,
    });


    SessionStatusAutomaton.getInstance().calcNewState(
      {
        state: rawWaStateStatus.state,
        stream: rawWaStateStatus.stream,
        hasSynced: rawWaStateStatus.hasSynced,
        qrcode: rawWaStateStatus.qrcode,
        // pairingCode has to travel too: the automaton's handlePairingCode branch is guarded on
        // it, so omitting it here dropped every generated code on the floor — the trigger reached
        // the session doc while the code itself never did.
        pairingCode: rawWaStateStatus.pairingCode,
        pairingCodeError: (rawWaStateStatus as { pairingCodeError?: string }).pairingCodeError,
        pairingMode: (rawWaStateStatus as { pairingMode?: boolean }).pairingMode,
      } as Parameters<typeof SessionStatusAutomaton.prototype.calcNewState>[0],
      triggerdBy,
      forceUpdate
    );
  }

  private getLocalSessionId(): string {
    let localSessionId = window.localStorage.getItem("localSessionId");
    if (!localSessionId) {
      localSessionId = ObjectId().toString();
      window.localStorage.setItem("localSessionId", localSessionId);
    }
    return localSessionId;
  }

}

export const waStateManager = WaStateManager.getInstance();

export const handleWaStateHealthCheck = async (rawWaStateStatus: RawWaStateStatus, sendResponse: SendResponseFunction): Promise<void> => {
  console.log('[wa-state-manager] (waStateHealthCheck) rawWaStateStatus:', rawWaStateStatus);
  WaStateManager.getInstance().handleWaStateChanges(rawWaStateStatus, "handleWaStateHealthCheck");
  sendResponse?.({});
}

export const appStateChanged = async (rawWaStateStatus: RawWaStateStatus): Promise<void> => {
  console.log('[wa-state-manager] (appStateChanged) rawWaStateStatus:', rawWaStateStatus);
  WaStateManager.getInstance().handleWaStateChanges(rawWaStateStatus, "appStateChanged");
}

export const handleQRChanged = async (rawWaStateStatus: RawWaStateStatus, sendResponse: SendResponseFunction): Promise<void> => {
  console.log('[wa-state-manager] (handleQRChanged) rawWaStateStatus:', rawWaStateStatus);

  const userId = selectUserId(store.getState() as object as CommonRootState);
  if (!userId) {
    await store.dispatch(initAuth());
  }

  WaStateManager.getInstance().handleWaStateChanges(rawWaStateStatus, "handleQRChanged", true);
  sendResponse?.({});
}

export const handlePairingCode = async (rawWaStateStatus: RawWaStateStatus, sendResponse: SendResponseFunction): Promise<void> => {
  console.log('[wa-state-manager] (handlePairingCode) rawWaStateStatus:', rawWaStateStatus);

  const userId = selectUserId(store.getState() as object as CommonRootState);
  if (!userId) {
    await store.dispatch(initAuth());
  }

  WaStateManager.getInstance().handleWaStateChanges(rawWaStateStatus, "handlePairingCode", true);
  sendResponse?.({});
}

export const handleWaLogout = async (rawWaStateStatus: RawWaStateStatus): Promise<void> => {
  console.log('[wa-state-manager] (handleWaLogout) rawWaStateStatus:', rawWaStateStatus);
  WaStateManager.getInstance().handleWaStateChanges(rawWaStateStatus, "handleWaLogout");
}


